import AXIOS_INSTANCE from './axiosInstance'
import type {
  AncFormTemplate,
  AncVisit,
  BookPregnancyInput,
  DeliveryRecord,
  PostnatalVisit,
  PregnancyEpisode,
  PregnancyStatus,
  RecordDeliveryInput,
  UpsertAncVisitInput,
  UpsertPostnatalVisitInput
} from '@type/antenatal.type'
import { GeneralResMock } from '@type/general.type'
import { ANC_API_RESOURCES } from '../pages/Antatal/api/ancApiContract'
import { DEFAULT_ANC_FIELDS } from '../pages/Antatal/utils/defaultFormTemplate'

const jsonApiHeaders = { 'Content-Type': 'application/vnd.api+json' }

type JsonApiResource = {
  id: string
  type: string
  attributes: Record<string, any>
  relationships?: Record<string, any>
}

const relId = (rel?: { data?: { id?: string } | null }) => rel?.data?.id

export function mapAncFormTemplate(raw: JsonApiResource): AncFormTemplate {
  return {
    id: String(raw.id),
    type: 'anc_form_template',
    attributes: {
      name: raw.attributes.name,
      facility_id: raw.attributes.facility_id ? String(raw.attributes.facility_id) : undefined,
      schedule_preset: raw.attributes.schedule_preset,
      is_default: Boolean(raw.attributes.is_default),
      fields: Array.isArray(raw.attributes.fields) ? raw.attributes.fields : [],
      created_at: raw.attributes.created_at,
      updated_at: raw.attributes.updated_at
    }
  }
}

export function mapDeliveryRecord(raw: JsonApiResource): DeliveryRecord {
  const pregnancyId = String(relId(raw.relationships?.pregnancy_episode) || raw.attributes.pregnancy_episode_id || '')
  const patientId = String(relId(raw.relationships?.patient) || raw.attributes.patient_id || '')
  return {
    id: String(raw.id),
    type: 'delivery_record',
    attributes: {
      pregnancy_id: pregnancyId,
      patient_id: patientId,
      delivered_at: raw.attributes.delivered_at,
      place: raw.attributes.place,
      mode: raw.attributes.mode || 'svd',
      outcome: raw.attributes.outcome || 'live_birth',
      sex: raw.attributes.sex,
      birth_weight_kg: raw.attributes.birth_weight_kg != null ? Number(raw.attributes.birth_weight_kg) : null,
      apgar_1: raw.attributes.apgar_1 != null ? Number(raw.attributes.apgar_1) : null,
      apgar_5: raw.attributes.apgar_5 != null ? Number(raw.attributes.apgar_5) : null,
      complications: raw.attributes.complications,
      notes: raw.attributes.notes,
      created_at: raw.attributes.created_at,
      updated_at: raw.attributes.updated_at
    }
  }
}

export function mapPostnatalVisit(raw: JsonApiResource): PostnatalVisit {
  const pregnancyId = String(relId(raw.relationships?.pregnancy_episode) || raw.attributes.pregnancy_episode_id || '')
  const patientId = String(relId(raw.relationships?.patient) || raw.attributes.patient_id || '')
  const encounterId = relId(raw.relationships?.encounter)
  return {
    id: String(raw.id),
    type: 'postnatal_visit',
    attributes: {
      pregnancy_id: pregnancyId,
      patient_id: patientId,
      encounter_id: encounterId ? String(encounterId) : null,
      visit_number: Number(raw.attributes.visit_number) || 1,
      contact_label: raw.attributes.contact_label,
      scheduled_date: String(raw.attributes.scheduled_date || '').slice(0, 10),
      completed_date: raw.attributes.completed_date ? String(raw.attributes.completed_date).slice(0, 10) : null,
      status: raw.attributes.status || 'scheduled',
      mother_findings: raw.attributes.mother_findings || {},
      newborn_findings: raw.attributes.newborn_findings || {},
      created_at: raw.attributes.created_at,
      updated_at: raw.attributes.updated_at
    }
  }
}

export function mapPregnancyEpisode(raw: JsonApiResource, included?: JsonApiResource[]): PregnancyEpisode {
  const visitRels = raw.relationships?.anc_visits?.data
  const visitData = Array.isArray(visitRels) ? visitRels : visitRels ? [visitRels] : []
  const tmplRel = raw.relationships?.anc_form_template?.data
  const patientId = String(relId(raw.relationships?.patient) || raw.attributes.patient_id || '')
  const status = (raw.attributes.status || 'active') as PregnancyStatus

  const deliveryRelId = relId(raw.relationships?.delivery_record)
  const deliveryFromIncluded =
    included && deliveryRelId
      ? included.find(i => i.type === 'delivery_record' && String(i.id) === String(deliveryRelId))
      : undefined

  const pnRels = raw.relationships?.postnatal_visits?.data
  const pnRelIds = Array.isArray(pnRels) ? pnRels : pnRels ? [pnRels] : []
  const postnatalFromIncluded =
    included && pnRelIds.length
      ? pnRelIds
          .map((r: { id: string }) => included.find(i => i.type === 'postnatal_visit' && String(i.id) === String(r.id)))
          .filter(Boolean)
          .map(r => mapPostnatalVisit(r!))
      : included
        ? included.filter(i => i.type === 'postnatal_visit').map(mapPostnatalVisit)
        : []

  return {
    id: String(raw.id),
    type: 'pregnancy_episode',
    attributes: {
      patient_id: patientId,
      patient_name: raw.attributes.patient_name || 'Patient',
      mrn: raw.attributes.mrn,
      anc_number: raw.attributes.anc_number,
      lmp: String(raw.attributes.lmp || '').slice(0, 10),
      edd: String(raw.attributes.edd || '').slice(0, 10),
      gravida: Number(raw.attributes.gravida) || 1,
      parity: Number(raw.attributes.parity) || 0,
      booking_date: String(raw.attributes.created_at || '').slice(0, 10),
      risk_level: raw.attributes.risk_level || 'low',
      risk_flags: Array.isArray(raw.attributes.risk_flags) ? raw.attributes.risk_flags : [],
      status,
      schedule_preset: raw.attributes.schedule_preset || 'who_8',
      notes: raw.attributes.notes,
      labour_started_at: raw.attributes.labour_started_at || null,
      delivery_at: raw.attributes.delivery_at || null,
      postnatal_started_at: raw.attributes.postnatal_started_at || null,
      created_at: raw.attributes.created_at,
      updated_at: raw.attributes.updated_at
    },
    relationships: {
      patient: { data: { id: patientId, type: 'patient' } },
      visits: { data: visitData.map((v: { id: string }) => ({ id: String(v.id), type: 'anc_visit' as const })) },
      form_template: tmplRel ? { data: { id: String(tmplRel.id), type: 'anc_form_template' as const } } : { data: null }
    },
    delivery_record: deliveryFromIncluded ? mapDeliveryRecord(deliveryFromIncluded) : undefined,
    postnatal_visits: postnatalFromIncluded.length ? postnatalFromIncluded : undefined
  }
}

export function mapAncVisit(raw: JsonApiResource): AncVisit {
  const pregnancyId = String(relId(raw.relationships?.pregnancy_episode) || raw.attributes.pregnancy_episode_id || '')
  const patientId = String(relId(raw.relationships?.patient) || raw.attributes.patient_id || '')
  const encounterId = relId(raw.relationships?.encounter)

  return {
    id: String(raw.id),
    type: 'anc_visit',
    attributes: {
      pregnancy_id: pregnancyId,
      patient_id: patientId,
      encounter_id: encounterId ? String(encounterId) : null,
      visit_number: Number(raw.attributes.visit_number) || 1,
      contact_label: raw.attributes.contact_label,
      scheduled_date: String(raw.attributes.scheduled_date || '').slice(0, 10),
      completed_date: raw.attributes.completed_date ? String(raw.attributes.completed_date).slice(0, 10) : null,
      gestational_age_weeks: raw.attributes.gestational_age_weeks ?? 0,
      gestational_age_days: raw.attributes.gestational_age_days ?? 0,
      status: raw.attributes.status || 'scheduled',
      findings: raw.attributes.findings || {},
      created_at: raw.attributes.created_at,
      updated_at: raw.attributes.updated_at
    },
    relationships: {
      pregnancy: { data: { id: pregnancyId, type: 'pregnancy_episode' } },
      patient: { data: { id: patientId, type: 'patient' } },
      encounter: encounterId ? { data: { id: String(encounterId), type: 'encounter' } } : { data: null }
    }
  }
}

export async function fetchAncFormTemplates(query?: string) {
  const { data } = await AXIOS_INSTANCE.get<GeneralResMock<JsonApiResource[]>>(ANC_API_RESOURCES.ancFormTemplates, {
    params: { ...(query ? { query } : {}), 'page[size]': 100 }
  })
  return (data.data || []).map(mapAncFormTemplate)
}

export async function createAncFormTemplate(name: string, fields = DEFAULT_ANC_FIELDS) {
  const { data } = await AXIOS_INSTANCE.post<GeneralResMock<JsonApiResource>>(
    ANC_API_RESOURCES.ancFormTemplates,
    {
      data: {
        type: 'anc_form_template',
        attributes: {
          name,
          schedule_preset: 'who_8',
          is_default: false,
          fields
        }
      }
    },
    { headers: jsonApiHeaders }
  )
  return mapAncFormTemplate(data.data)
}

export async function updateAncFormTemplate(template: AncFormTemplate) {
  const { data } = await AXIOS_INSTANCE.patch<GeneralResMock<JsonApiResource>>(
    `${ANC_API_RESOURCES.ancFormTemplates}/${template.id}`,
    {
      data: {
        type: 'anc_form_template',
        id: template.id,
        attributes: {
          name: template.attributes.name,
          schedule_preset: template.attributes.schedule_preset,
          is_default: template.attributes.is_default,
          fields: template.attributes.fields
        }
      }
    },
    { headers: jsonApiHeaders }
  )
  return mapAncFormTemplate(data.data)
}

export async function fetchPregnancyEpisodes(params?: {
  patient_id?: string
  status?: string
  risk_level?: string
  query?: string
}) {
  const { data } = await AXIOS_INSTANCE.get<GeneralResMock<JsonApiResource[]> & { included?: JsonApiResource[] }>(
    ANC_API_RESOURCES.pregnancyEpisodes,
    {
      params: {
        ...params,
        'page[size]': 100,
        include: 'patient,anc_form_template,anc_visits,delivery_record,postnatal_visits'
      }
    }
  )
  const included = data.included || []
  return (data.data || []).map(raw => mapPregnancyEpisode(raw, included))
}

export async function fetchPregnancyEpisode(id: string) {
  const { data } = await AXIOS_INSTANCE.get<GeneralResMock<JsonApiResource> & { included?: JsonApiResource[] }>(
    `${ANC_API_RESOURCES.pregnancyEpisodes}/${id}`,
    {
      params: {
        include: 'patient,anc_form_template,anc_visits,delivery_record,postnatal_visits'
      }
    }
  )
  return mapPregnancyEpisode(data.data, data.included || [])
}

export async function bookPregnancyEpisode(input: BookPregnancyInput) {
  const { data } = await AXIOS_INSTANCE.post<GeneralResMock<JsonApiResource>>(
    ANC_API_RESOURCES.pregnancyEpisodes,
    {
      data: {
        type: 'pregnancy_episode',
        attributes: {
          patient_id: input.patient_id,
          anc_number: input.anc_number,
          lmp: input.lmp,
          gravida: input.gravida,
          parity: input.parity,
          risk_level: input.risk_level,
          risk_flags: input.risk_flags || [],
          schedule_preset: input.schedule_preset,
          notes: input.notes,
          anc_form_template_id: input.form_template_id
        }
      }
    },
    { headers: jsonApiHeaders }
  )
  return mapPregnancyEpisode(data.data)
}

export async function updatePregnancyEpisode(
  id: string,
  attrs: {
    risk_level?: string
    risk_flags?: string[]
    notes?: string
    status?: string
    schedule_preset?: string
    anc_form_template_id?: string
  }
) {
  const { data } = await AXIOS_INSTANCE.patch<GeneralResMock<JsonApiResource>>(
    `${ANC_API_RESOURCES.pregnancyEpisodes}/${id}`,
    {
      data: {
        type: 'pregnancy_episode',
        id,
        attributes: attrs
      }
    },
    { headers: jsonApiHeaders }
  )
  return mapPregnancyEpisode(data.data)
}

export async function startLabour(pregnancyId: string) {
  const { data } = await AXIOS_INSTANCE.post<GeneralResMock<JsonApiResource> & { included?: JsonApiResource[] }>(
    `${ANC_API_RESOURCES.pregnancyEpisodes}/${pregnancyId}/start_labour`,
    {},
    { headers: jsonApiHeaders }
  )
  return mapPregnancyEpisode(data.data, data.included)
}

export async function recordDelivery(pregnancyId: string, input: RecordDeliveryInput) {
  const { data } = await AXIOS_INSTANCE.post<GeneralResMock<JsonApiResource> & { included?: JsonApiResource[] }>(
    `${ANC_API_RESOURCES.pregnancyEpisodes}/${pregnancyId}/record_delivery`,
    {
      data: {
        type: 'delivery_record',
        attributes: input
      }
    },
    { headers: jsonApiHeaders }
  )
  return mapPregnancyEpisode(data.data, data.included)
}

export async function completePregnancyEpisode(pregnancyId: string) {
  const { data } = await AXIOS_INSTANCE.post<GeneralResMock<JsonApiResource> & { included?: JsonApiResource[] }>(
    `${ANC_API_RESOURCES.pregnancyEpisodes}/${pregnancyId}/complete`,
    {},
    { headers: jsonApiHeaders }
  )
  return mapPregnancyEpisode(data.data, data.included)
}

export async function fetchAncVisits(params?: { patient_id?: string; pregnancy_episode_id?: string; status?: string }) {
  const { data } = await AXIOS_INSTANCE.get<GeneralResMock<JsonApiResource[]>>(ANC_API_RESOURCES.ancVisits, {
    params: {
      ...params,
      'page[size]': 100,
      include: 'pregnancy_episode,patient,encounter'
    }
  })
  return (data.data || []).map(mapAncVisit)
}

export async function fetchAncVisit(id: string) {
  const { data } = await AXIOS_INSTANCE.get<GeneralResMock<JsonApiResource>>(`${ANC_API_RESOURCES.ancVisits}/${id}`, {
    params: { include: 'pregnancy_episode,patient,encounter' }
  })
  return mapAncVisit(data.data)
}

export async function patchAncVisit(visitId: string, input: UpsertAncVisitInput) {
  const { data } = await AXIOS_INSTANCE.patch<GeneralResMock<JsonApiResource>>(
    `${ANC_API_RESOURCES.ancVisits}/${visitId}`,
    {
      data: {
        type: 'anc_visit',
        id: visitId,
        attributes: {
          ...(input.findings !== undefined ? { findings: input.findings } : {}),
          ...(input.status ? { status: input.status } : {}),
          ...(input.status === 'completed' ? { completed_date: new Date().toISOString().slice(0, 10) } : {})
        }
      }
    },
    { headers: jsonApiHeaders }
  )
  return mapAncVisit(data.data)
}

export async function startAncVisit(visitId: string) {
  const { data } = await AXIOS_INSTANCE.post<GeneralResMock<JsonApiResource>>(
    `${ANC_API_RESOURCES.ancVisits}/${visitId}/start`,
    {},
    { headers: jsonApiHeaders }
  )
  return mapAncVisit(data.data)
}

export async function fetchPostnatalVisits(params?: {
  patient_id?: string
  pregnancy_episode_id?: string
  status?: string
}) {
  const { data } = await AXIOS_INSTANCE.get<GeneralResMock<JsonApiResource[]>>(ANC_API_RESOURCES.postnatalVisits, {
    params: {
      ...params,
      'page[size]': 100,
      include: 'pregnancy_episode,patient,encounter'
    }
  })
  return (data.data || []).map(mapPostnatalVisit)
}

export async function fetchPostnatalVisit(id: string) {
  const { data } = await AXIOS_INSTANCE.get<GeneralResMock<JsonApiResource>>(
    `${ANC_API_RESOURCES.postnatalVisits}/${id}`,
    {
      params: { include: 'pregnancy_episode,patient,encounter' }
    }
  )
  return mapPostnatalVisit(data.data)
}

export async function patchPostnatalVisit(visitId: string, input: UpsertPostnatalVisitInput) {
  const { data } = await AXIOS_INSTANCE.patch<GeneralResMock<JsonApiResource>>(
    `${ANC_API_RESOURCES.postnatalVisits}/${visitId}`,
    {
      data: {
        type: 'postnatal_visit',
        id: visitId,
        attributes: {
          ...(input.mother_findings !== undefined ? { mother_findings: input.mother_findings } : {}),
          ...(input.newborn_findings !== undefined ? { newborn_findings: input.newborn_findings } : {}),
          ...(input.status ? { status: input.status } : {}),
          ...(input.status === 'completed'
            ? { completed_date: input.completed_date || new Date().toISOString().slice(0, 10) }
            : {})
        }
      }
    },
    { headers: jsonApiHeaders }
  )
  return mapPostnatalVisit(data.data)
}
